import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, GitMerge, Download, Search, X, ChevronLeft, ChevronRight, Trash2, ArrowUpRight, UserPlus, Copy, Upload, SkipForward, ShieldCheck, Mail, Phone, Calendar, ShoppingBag, Clock, UserCircle2, Target, FlaskConical, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import PhoneInput from "@/components/forms/PhoneInput";
import { customerHasUpdates } from "@/lib/customerViewLog";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import ImportCustomersDialog from "./ImportCustomersDialog";
import GoalTab from "./goal/GoalTab";
import ReviewQueueTab from "./customer/ReviewQueueTab";
import SafeMergeDialog from "./customer/SafeMergeDialog";

// Brand-level customer directory.
//  • Manage Customers – blank until the operator searches; supports Excel/CSV
//    export of the current search results.
//  • Find Duplicates  – groups rows sharing email / phone / normalised name.
export default function BrandCustomers() {
  const [params, setParams] = useSearchParams();
  const urlTab = params.get("tab");
  const initialTab = ["duplicates", "review", "goal"].includes(urlTab) ? urlTab : "manage";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    // Keep URL query in sync so linking back from /customers/merge lands on
    // the correct tab and refreshes preserve the operator's context.
    if (tab !== "manage" && params.get("tab") !== tab) {
      setParams({ tab }, { replace: true });
    } else if (tab === "manage" && params.get("tab")) {
      setParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  // iter-f1 — the duplicates tab now fetches pre-paired candidates from the
  // backend; no need to pre-fetch the full directory here. We still load the
  // branches list because other children (e.g. ManageCustomersTab) use it.
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    (async () => {
      try { const r = await staffApi.get("/branches"); setBranches(r.data || []); }
      catch { /* silent */ }
    })();
  }, []);

  return (
    <StaffShell context="brand">
      <div className="mb-4">
        <p className="tracking-overline text-xs text-muted-foreground">BRAND CATALOG</p>
        <h1 className="text-3xl font-semibold mt-1" data-testid="customers-title">Customers</h1>
        <p className="text-sm text-muted-foreground">Brand-wide customer database. Visits and orders roll up across every location.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-transparent p-0 border-b border-border rounded-none w-full justify-start gap-1 h-auto">
          <TabsTrigger
            value="manage"
            data-testid="tab-manage-customers"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 gap-2"
          >
            <Users className="w-4 h-4" /> Manage Customers
          </TabsTrigger>
          <TabsTrigger
            value="duplicates"
            data-testid="tab-find-duplicates"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 gap-2"
          >
            <GitMerge className="w-4 h-4" /> Find Duplicates
          </TabsTrigger>
          <TabsTrigger
            value="review"
            data-testid="tab-review-queue"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 gap-2"
          >
            <FlaskConical className="w-4 h-4" /> Review Queue
          </TabsTrigger>
          <TabsTrigger
            value="goal"
            data-testid="tab-goal"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 gap-2"
          >
            <Target className="w-4 h-4" /> G.O.A.L
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manage" className="mt-4">
          <ManageCustomersTab />
        </TabsContent>

        <TabsContent value="duplicates" className="mt-4">
          <FindDuplicatesTab active={tab === "duplicates"} />
        </TabsContent>

        <TabsContent value="review" className="mt-4">
          <ReviewQueueTab active={tab === "review"} />
        </TabsContent>

        <TabsContent value="goal" className="mt-4">
          <GoalTab active={tab === "goal"} />
        </TabsContent>
      </Tabs>
    </StaffShell>
  );
}

/* --------------- Manage Customers tab --------------- */
// Blank-until-search: no rows are shown until the operator types at least
// one character. This keeps the initial payload small and matches the user's
// requested workflow (search-first list).
function ManageCustomersTab() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const v = parseInt(localStorage.getItem("brand-cust-rpp") || "50", 10);
    return [10, 25, 50, 100].includes(v) ? v : 50;
  });
  useEffect(() => { localStorage.setItem("brand-cust-rpp", String(rowsPerPage)); }, [rowsPerPage]);
  useEffect(() => { setPage(1); }, [q, rowsPerPage]);

  useEffect(() => {
    (async () => {
      try {
        const r = await staffApi.get("/branches");
        setBranches(r.data || []);
      } catch { /* silent */ }
    })();
  }, []);
  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);

  // Server-side search with a small debounce. When the query is empty we still
  // fetch ALL customers (blank state was dropped in favour of always-on
  // browsing, matching the operator's request).
  // `reloadNonce` bumps to force a re-fetch after a customer is created,
  // deleted, or merged elsewhere — the operator sees the fresh list without
  // needing a manual browser refresh.
  const [reloadNonce, setReloadNonce] = useState(0);
  const reloadRows = useCallback(() => {
    // Jump to page 1 so the freshly-added customer (now on top after the
    // created_at-desc sort in the load effect) is visible right away in the
    // default 10-row page — no manual pagination or "Rows per page" bump.
    setPage(1);
    setReloadNonce((n) => n + 1);
  }, []);
  useEffect(() => {
    const needle = q.trim();
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        // Iter-332 — Server-side pagination. Send `page` + `per_page` so the
        // backend loads ONLY the current window (50 by default) instead of
        // shipping every guest across the wire. The server also sorts by
        // `created_at` desc so a freshly-created customer lands on page 1 —
        // no more client-side re-sort needed.
        const params = { page, per_page: rowsPerPage };
        if (needle) params.search = needle;
        const r = await staffApi.get("/customers", { params });
        // Endpoint returns `{rows, total, page, per_page}` when paginated.
        const payload = r.data && Array.isArray(r.data.rows) ? r.data : { rows: r.data || [], total: (r.data || []).length };
        if (!cancelled) {
          setRows(payload.rows || []);
          setTotal(typeof payload.total === "number" ? payload.total : (payload.rows || []).length);
        }
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, needle ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, reloadNonce, page, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const clampedPage = Math.min(page, totalPages);
  const paged = rows; // server already returned exactly the current page

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.full_name}"? This cannot be undone.`)) return;
    try {
      await staffApi.delete(`/customers/${c.id}`);
      toast.success("Customer deleted");
      // Iter-332 — With server-side pagination the row count + total both
      // live on the server, so a delete triggers a fresh page fetch rather
      // than a client-side splice (which would leave `total` stale and
      // desync the pagination footer).
      setReloadNonce((n) => n + 1);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // CSV/Excel export. We serialise the CURRENT search results (client-side
  // filtered) into a UTF-8 BOM'd CSV so Excel opens it with the correct
  // encoding on both mac and Windows. Users asked for "Export to Excel"; CSV
  // is Excel-friendly and needs no extra dependency.
  const exportExcel = () => {
    if (rows.length === 0) { toast.error("Nothing to export — search for customers first"); return; }
    const headers = ["Location", "First Name", "Last Name", "Street 1", "Street 2", "State", "City", "Postal Code", "Email", "Primary Phone"];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      // Excel-safe: wrap when contains comma / quote / newline.
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    for (const c of rows) {
      lines.push([
        branchMap[c.primary_branch_id]?.name || "",
        c.first_name || (c.full_name || "").split(" ")[0] || "",
        c.last_name || (c.full_name || "").split(" ").slice(1).join(" ") || "",
        c.street_1 || "",
        c.street_2 || "",
        c.state || "",
        c.city || "",
        c.postal_code || "",
        c.email || "",
        c.whatsapp || "",
      ].map(esc).join(","));
    }
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `cocoon-customers-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} customers`);
  };

  const firstOf = (c) => c.first_name || (c.full_name || "").split(" ")[0] || "";
  const lastOf = (c) => c.last_name || (c.full_name || "").split(" ").slice(1).join(" ") || "";

  return (
    <>
      <Card className="rounded-lg mb-3">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[260px] max-w-md">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, email or phone…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid="cust-search-input"
                autoFocus
              />
              {q && (
                <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="cust-search-clear">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <div className="text-xs text-muted-foreground" data-testid="cust-result-count">
                {loading ? "Loading…" : (
                  <>Results <span className="font-medium text-foreground">{total === 0 ? 0 : (clampedPage - 1) * rowsPerPage + 1}
                  –{Math.min(clampedPage * rowsPerPage, total)}</span> of <span className="font-medium text-foreground">{total}</span></>
                )}
              </div>
              <Button size="sm" onClick={() => setNewOpen(true)} className="h-8 gap-1" data-testid="new-customer-btn">
                <UserPlus className="w-3.5 h-3.5" /> New Customer
              </Button>
              <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} className="h-8 gap-1" data-testid="import-csv-btn">
                <Upload className="w-3.5 h-3.5" /> Import CSV
              </Button>
              <Button size="sm" variant="outline" onClick={exportExcel} disabled={rows.length === 0} className="h-8 gap-1" data-testid="cust-export-btn">
                <Download className="w-3.5 h-3.5" /> Export to Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-lg">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="h-8">
                    <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Location</TableHead>
                    <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">First Name</TableHead>
                    <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Last Name</TableHead>
                    <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Street 1</TableHead>
                    <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Street 2</TableHead>
                    <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">State</TableHead>
                    <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">City</TableHead>
                    <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Postal</TableHead>
                    <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Email</TableHead>
                    <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Primary Phone</TableHead>
                    <TableHead className="py-1.5 text-[11px] uppercase tracking-wider text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center text-xs text-muted-foreground py-8" data-testid="cust-empty-row">No customers match your search.</TableCell>
                    </TableRow>
                  )}
                  {paged.map((c) => (
                    <TableRow key={c.id} className="h-9 hover:bg-muted/40" data-testid={`customer-row-${c.id}`}>
                      <TableCell className="py-1.5 text-xs">
                        <Link
                          to={`/staff/brand/customers/${c.id}`}
                          className="text-primary hover:underline"
                          data-testid={`customer-location-link-${c.id}`}
                        >
                          {branchMap[c.primary_branch_id]?.name || "—"}
                        </Link>
                      </TableCell>
                      <TableCell className="py-1.5 text-sm font-medium">
                        <div className="flex items-center gap-1.5">
                          <span>{firstOf(c) || "—"}</span>
                          {customerHasUpdates(c) && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200 text-[9px] uppercase tracking-wider font-medium"
                              title="Medical form or guest preferences updated since you last opened this profile"
                              data-testid={`cust-update-badge-${c.id}`}
                            >New</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-1.5 text-sm">{lastOf(c) || "—"}</TableCell>
                      <TableCell className="py-1.5 text-xs text-muted-foreground">{c.street_1 || ""}</TableCell>
                      <TableCell className="py-1.5 text-xs text-muted-foreground">{c.street_2 || ""}</TableCell>
                      <TableCell className="py-1.5 text-xs text-muted-foreground">{c.state || ""}</TableCell>
                      <TableCell className="py-1.5 text-xs text-muted-foreground">{c.city || ""}</TableCell>
                      <TableCell className="py-1.5 text-xs text-muted-foreground">{c.postal_code || ""}</TableCell>
                      <TableCell className="py-1.5 text-xs text-muted-foreground">{c.email || "—"}</TableCell>
                      <TableCell className="py-1.5 text-xs text-muted-foreground whitespace-nowrap">{c.whatsapp || "—"}</TableCell>
                      <TableCell className="py-1 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => navigate(`/staff/brand/customers/${c.id}`)} className="h-7 gap-1 text-xs" data-testid={`customer-review-${c.id}`}>
                            <ArrowUpRight className="w-3 h-3" /> Review
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => remove(c)} className="h-7 w-7 text-destructive hover:text-destructive" data-testid={`customer-delete-${c.id}`} title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {total > 0 && (
              <div className="flex items-center justify-between px-3 py-2 border-t border-border text-xs" data-testid="cust-pagination">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span>Rows per page</span>
                  <Select value={String(rowsPerPage)} onValueChange={(v) => setRowsPerPage(parseInt(v, 10))}>
                    <SelectTrigger className="h-7 w-[70px] text-xs" data-testid="cust-rows-per-page"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[10, 25, 50, 100].map((n) => (<SelectItem key={n} value={String(n)}>{n}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Page {clampedPage} of {totalPages}</span>
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={clampedPage <= 1} data-testid="cust-page-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={clampedPage >= totalPages} data-testid="cust-page-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

      <NewCustomerDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onSaved={reloadRows}
        onCreated={(cust) => {
          setNewOpen(false);
          // Land the operator straight on the profile they just created.
          navigate(`/staff/brand/customers/${cust.id}`);
        }}
      />
      <ImportCustomersDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={reloadRows}
      />
    </>
  );
}

/* --------------- Find Duplicates tab --------------- */
// iter-f1 — pair-by-pair review. The server pre-pairs candidates by real
// email / canonical phone (placeholder `none@none.com` is excluded) and we
// show one pair at a time side-by-side with enough context to judge whether
// the two profiles are the same person. Three actions per pair:
//   • Merge             → opens the existing interactive merge screen
//   • Not a Duplicate   → persistently dismisses the pair (survives refresh)
//   • Skip              → session-only — the pair re-appears on next load
function FindDuplicatesTab({ active }) {
  const navigate = useNavigate();
  const { staff } = useAuth();
  // Merges and dismissals are both gated by the same "Customers · Delete"
  // permission so Care-Team staff see a locked state instead of inert
  // buttons. The backend enforces the same rule.
  const canMerge = hasPermission(staff, "brand.customers.delete");

  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [safeOpen, setSafeOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await staffApi.get("/customers/duplicates/pairs");
      setPairs(Array.isArray(r.data?.pairs) ? r.data.pairs : []);
      setIdx(0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (active) load(); }, [active, load]);

  // Manual search — filters the pair queue by name, email or phone on either
  // side of the pair so an operator can jump straight to a specific guest
  // instead of clicking Skip hundreds of times.
  const norm = (s) => String(s || "").toLowerCase();
  const digits = (s) => String(s || "").replace(/[^0-9]/g, "");
  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return pairs;
    const lq = norm(q);
    const dq = digits(q);
    const hit = (p) =>
      norm(p?.full_name).includes(lq) ||
      norm(p?.email).includes(lq) ||
      (dq.length >= 3 && digits(p?.phone).includes(dq));
    return pairs.filter((pair) => hit(pair.a) || hit(pair.b));
  }, [pairs, query]);

  const current = filtered[idx] || null;
  const remaining = filtered.length;

  useEffect(() => { setIdx(0); }, [query]);

  const dropCurrent = () => {
    if (!current) return;
    const key = current.pair_key;
    setPairs((prev) => prev.filter((p) => p.pair_key !== key));
    setIdx((i) => (i >= filtered.length - 1 ? Math.max(0, filtered.length - 2) : i));
  };

  const handleSkip = () => {
    if (!current) return;
    // Session-only: drop the current pair from the local list without
    // persisting anything — it will resurface on next load / refresh.
    dropCurrent();
  };

  const handleDismiss = async () => {
    if (!current || !canMerge) return;
    setBusy(true);
    try {
      await staffApi.post("/customers/duplicates/dismiss", { pair_key: current.pair_key });
      toast.success("Marked as not a duplicate");
      dropCurrent();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleMerge = () => {
    if (!current || !canMerge) return;
    navigate(`/staff/brand/customers/merge?ids=${current.a.id},${current.b.id}`);
  };

  return (
    <div data-testid="dupe-pair-view">
      <Card className="rounded-lg mb-3">
        <CardContent className="p-3 flex items-center gap-3">
          <GitMerge className="w-4 h-4 text-primary" />
          <div className="min-w-0">
            <p className="text-sm font-medium" data-testid="dupe-progress">
              {loading
                ? "Scanning for duplicates…"
                : remaining === 0
                  ? (query ? `No pairs match “${query}”` : "No duplicate pairs to review")
                  : `${remaining} ${remaining === 1 ? "pair" : "pairs"} ${query ? `matching “${query}”` : "remaining"}`}
            </p>
            <p className="text-[11px] text-muted-foreground">
              Review each pair side-by-side. Placeholder emails (<code className="font-mono">none@none.com</code>) are ignored.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative w-[260px]">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-8 h-8"
                placeholder="Search name, email or phone"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="dupe-search-input"
              />
              {query && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setQuery("")}
                  data-testid="dupe-search-clear"
                ><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
            {canMerge && (
              <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setSafeOpen(true)} disabled={loading || busy} data-testid="dupe-safe-merge-btn">
                <Zap className="w-3.5 h-3.5" /> Bulk merge safe pairs
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-8" onClick={load} disabled={loading || busy} data-testid="dupe-refresh-btn">
              {loading ? "Loading…" : "Refresh"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {!loading && remaining === 0 && (
        <Card className="rounded-lg">
          <CardContent className="p-8 text-center" data-testid="dupe-empty-state">
            <ShieldCheck className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
            <p className="text-sm font-medium">Your customer list is clean.</p>
            <p className="text-xs text-muted-foreground mt-1">Any new matches on real email or phone will appear here.</p>
          </CardContent>
        </Card>
      )}

      {current && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid={`dupe-pair-${current.pair_key}`}>
            <ProfileCard profile={current.a} label="Profile A" testId="dupe-profile-a" />
            <ProfileCard profile={current.b} label="Profile B" testId="dupe-profile-b" />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 justify-between">
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <span className="tracking-overline uppercase">Match on</span>
              {(current.match_on || []).map((m) => (
                <span key={m} className="px-1.5 py-0.5 rounded-full bg-secondary text-foreground font-medium capitalize" data-testid={`dupe-match-${m}`}>{m}</span>
              ))}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1"
                onClick={handleSkip}
                disabled={busy}
                data-testid="dupe-skip-btn"
                title="Hide this pair for now — it will come back next time you load the page"
              >
                <SkipForward className="w-3.5 h-3.5" /> Skip
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={handleDismiss}
                disabled={busy || !canMerge}
                data-testid="dupe-dismiss-btn"
                title={canMerge ? "Permanently mark as not a duplicate" : "Requires Customers · Delete permission"}
              >
                <X className="w-3.5 h-3.5" /> Not a Duplicate
              </Button>
              {canMerge ? (
                <Button
                  size="sm"
                  className="h-8 gap-1"
                  onClick={handleMerge}
                  disabled={busy}
                  data-testid="dupe-merge-btn"
                >
                  <GitMerge className="w-3.5 h-3.5" /> Merge
                </Button>
              ) : (
                <span
                  className="inline-flex items-center h-8 px-2 rounded-md border border-dashed border-border text-[11px] text-muted-foreground"
                  data-testid="dupe-merge-locked"
                  title="Merging deletes source records. Ask an admin to grant Customers · Delete."
                >
                  Merge locked — needs Customers · Delete
                </span>
              )}
            </div>
          </div>
        </>
      )}

      <SafeMergeDialog open={safeOpen} onOpenChange={setSafeOpen} onDone={load} />
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return String(iso); }
}

function ProfileCard({ profile, label, testId }) {
  const p = profile || {};
  const emailIsPlaceholder = (p.email || "").trim().toLowerCase() === "none@none.com";
  return (
    <Card className="rounded-lg" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="tracking-overline text-[10px] text-muted-foreground">{label}</span>
          <Link
            to={`/staff/brand/customers/${p.id}`}
            className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
            target="_blank"
            rel="noreferrer"
            data-testid={`${testId}-open-profile`}
          >
            Open profile <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center shrink-0">
            <UserCircle2 className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold truncate" data-testid={`${testId}-name`}>{p.full_name || "—"}</p>
            <div className="mt-2 grid gap-1.5 text-xs">
              <Row icon={<Mail className="w-3.5 h-3.5" />} testId={`${testId}-email`}>
                {p.email ? (
                  <span className={emailIsPlaceholder ? "text-muted-foreground italic" : ""}>{p.email}{emailIsPlaceholder ? " (placeholder)" : ""}</span>
                ) : "—"}
              </Row>
              <Row icon={<Phone className="w-3.5 h-3.5" />} testId={`${testId}-phone`}>{p.phone || "—"}</Row>
              <Row icon={<Calendar className="w-3.5 h-3.5" />} testId={`${testId}-appointments`}>
                <span className="font-medium text-foreground">{p.appointments_count || 0}</span> appointment{(p.appointments_count || 0) === 1 ? "" : "s"}
              </Row>
              <Row icon={<ShoppingBag className="w-3.5 h-3.5" />} testId={`${testId}-orders`}>
                <span className="font-medium text-foreground">{p.orders_count || 0}</span> order{(p.orders_count || 0) === 1 ? "" : "s"}
              </Row>
              <Row icon={<Clock className="w-3.5 h-3.5" />} testId={`${testId}-last-visit`}>
                Last visit · <span className="text-foreground">{fmtDate(p.last_visit)}</span>
              </Row>
              <Row icon={<UserPlus className="w-3.5 h-3.5" />} testId={`${testId}-registered`}>
                Registered · <span className="text-foreground">{fmtDate(p.created_at)}</span>
              </Row>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ icon, children, testId }) {
  return (
    <div className="flex items-center gap-2 text-muted-foreground" data-testid={testId}>
      <span className="text-muted-foreground/70 shrink-0">{icon}</span>
      <span className="truncate">{children}</span>
    </div>
  );
}


/* --------------- New Customer dialog --------------- */
// Staff creates a guest profile on the fly. The server generates a random
// password so the guest can log in immediately. A checkbox lets the operator
// email the credentials directly. After success the generated password is
// shown to the operator (with a copy button) so they can hand it out verbally.
function NewCustomerDialog({ open, onOpenChange, onCreated, onSaved }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", whatsapp: "", email_credentials: true });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { customer, generated_password, email_delivered }

  useEffect(() => {
    if (open) {
      setForm({ first_name: "", last_name: "", email: "", whatsapp: "", email_credentials: true });
      setResult(null);
    }
  }, [open]);

  const submit = async () => {
    if (!form.first_name.trim()) { toast.error("First name is required"); return; }
    if (!form.email.trim() && !form.whatsapp.trim()) { toast.error("Please provide either an Email or a WhatsApp number to continue."); return; }
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || "",
        email: form.email.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        email_credentials: !!form.email_credentials && !!form.email.trim(),
      };
      const r = await staffApi.post("/customers", payload);
      setResult(r.data);
      toast.success("Customer created");
      // Refresh the underlying Manage Customers list so the new row is
      // visible immediately — the operator no longer needs to hit browser
      // refresh to see the customer they just created.
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(result.generated_password);
      toast.success("Password copied");
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-customer-dialog">
        <DialogHeader>
          <DialogTitle>New customer</DialogTitle>
          <DialogDescription>Create a guest profile. We&rsquo;ll auto-generate a login password so they can sign in right away.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="new-cust-success">
              <p className="font-medium text-emerald-900">Account ready</p>
              <p className="text-xs text-emerald-800 mt-1">Share these credentials with <strong>{result.customer.full_name}</strong>. {result.email_delivered ? "A welcome email was sent." : (form.email_credentials && form.email ? "Email could not be delivered — please share manually." : "Email delivery was skipped.")}</p>
              <div className="mt-3 grid gap-1 text-xs">
                {result.customer.email && <div><span className="text-muted-foreground">Email · </span><span className="font-mono">{result.customer.email}</span></div>}
                {result.customer.whatsapp && <div><span className="text-muted-foreground">WhatsApp · </span><span className="font-mono">{result.customer.whatsapp}</span></div>}
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-muted-foreground">Password · </span>
                  <code className="font-mono text-sm bg-white border border-emerald-200 rounded px-2 py-0.5" data-testid="new-cust-password">{result.generated_password}</code>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={copyPassword} data-testid="copy-password-btn"><Copy className="w-3 h-3" /></Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onCreated(result.customer)} data-testid="new-cust-open-profile">Open profile</Button>
              <Button onClick={() => onOpenChange(false)} data-testid="new-cust-close">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First name</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="new-cust-first" autoFocus />
              </div>
              <div>
                <Label>Last name</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="new-cust-last" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-cust-email" />
              </div>
              <div>
                <Label>WhatsApp / phone</Label>
                <PhoneInput value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} testId="new-cust-whatsapp" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm select-none">
              <input
                type="checkbox"
                className="h-4 w-4 accent-rose-600"
                checked={!!form.email_credentials}
                onChange={(e) => setForm({ ...form, email_credentials: e.target.checked })}
                disabled={!form.email.trim()}
                data-testid="new-cust-email-creds"
              />
              Email the auto-generated password to this customer
              {!form.email.trim() && <span className="text-[11px] text-muted-foreground">(requires an email)</span>}
            </label>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="new-cust-cancel">Cancel</Button>
              <Button onClick={submit} disabled={busy} data-testid="new-cust-submit">{busy ? "Creating…" : "Create customer"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
